<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import type { TextColor } from '@uiKit/elements/Text.vue'
import Skeleton from '@uiKit/elements/Skeleton.vue'
import StatusPill from './StatusPill.vue'
import { formatFixedAmount } from '../../utils/moneyDisplayUtils'
import type { PriceTile, TileTone } from '../../utils/requestPriceTileUtils'

// The price tiles under a detail header, one column per tile. On the request:
// what the seller asked, what Sliceal proposed (or agreed), and what reaches
// them after fees. On the property it becomes: the agreed price and what has
// been raised. A figure that does not exist yet shows "—" with a line saying
// when it will — never a hidden tile, which makes the row jump. Tiles arrive
// worded from each screen's builder (`buildPriceTiles`, `buildPropertyPriceTiles`).
const props = withDefaults(
  defineProps<{
    tiles: PriceTile<string>[]
    currencyCode: string
    /** The net payout is still being quoted — a skeleton in its place. */
    isNetLoading?: boolean
  }>(),
  { isNetLoading: false },
)

const VALUE_COLOR: Record<TileTone, TextColor> = {
  neutral: 'default',
  primary: 'primary',
  success: 'success',
  danger: 'danger',
}

const HINT_COLOR = {
  success: 'success',
  danger: 'danger',
  muted: 'muted',
} as const satisfies Record<string, TextColor>
</script>

<template>
  <Box is="section" class="summary" aria-label="Price summary">
    <Box
      v-for="tile in tiles"
      :key="tile.key"
      direction="col"
      gap="0.375rem"
      :class="['summary__tile', `summary__tile--${tile.tone}`]"
    >
      <Box flex align="center" justify="between" gap="2" class="summary__top">
        <Text is="span" type="caption-sm" weight="semibold" transform="uppercase" color="default">
          {{ tile.label }}
        </Text>
        <StatusPill v-if="tile.tag" :label="tile.tag.label" :tone="tile.tag.tone" />
      </Box>

      <Skeleton v-if="tile.key === 'net' && isNetLoading" width="10rem" height="1.75rem" />
      <Text
        v-else
        is="span"
        type="caption-sm"
        weight="semibold"
        :color="VALUE_COLOR[tile.tone]"
        class="summary__value"
      >
        {{ currencyCode }}
        <Text is="span" type="title-lg" :color="VALUE_COLOR[tile.tone]" class="summary__amount">
          {{ tile.amount === null ? '—' : formatFixedAmount(tile.amount) }}
        </Text>
      </Text>

      <Text v-if="tile.hint" is="span" type="caption" :color="HINT_COLOR[tile.hint.tone]">
        {{ tile.hint.label }}
      </Text>
      <Text v-if="tile.note" is="span" type="caption" color="muted">{{ tile.note }}</Text>
    </Box>
  </Box>
</template>

<style scoped>
.summary {
  display: grid;
  grid-template-columns: repeat(v-bind('props.tiles.length'), minmax(0, 1fr));
  gap: 1rem;
}

.summary__tile {
  min-width: 0;
  padding: 0.75rem 0.875rem;
  border: 1px solid var(--p-content-border-color);
  border-radius: 12px;
  background: var(--p-surface-0);
}

.summary__top {
  min-height: 1.375rem;
}

.summary__tile--primary {
  border-color: var(--p-primary-200);
  background: var(--p-primary-50);
}

.summary__tile--success {
  border-color: var(--p-success-200);
  background: var(--p-success-50);
}

.summary__tile--danger {
  border-color: var(--p-danger-200);
  background: var(--p-danger-50);
}

.summary__value {
  line-height: normal;
  white-space: nowrap;
}

.summary__amount {
  letter-spacing: -0.3px;
}

@media (max-width: 992px) {
  .summary {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
